import { useEffect, useMemo, useState, Fragment, useRef } from "react";
import { Navigate } from "react-router-dom";
import { Search, Wrench, Package, PackageX, ChevronDown, Download, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { mediaUrl } from "./ProductsPage";
import { useAuth } from "../context/AuthContext";
import { CustomCategoriesSection } from "../components/CustomCategoriesSection";
import { HubSettingsSection } from "../components/HubSettingsSection";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const ALL = "__all__";

export default function SettingsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(ALL);
  const [kindFilter, setKindFilter] = useState(ALL);
  const [openCats, setOpenCats] = useState({});

  useEffect(() => {
    api.get("/settings/product-kinds")
      .then((r) => setItems(r.data.data))
      .catch(() => toast.error("Ürün listesi yüklenemedi"))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => [...new Set(items.map((i) => i.category))], [items]);

  const visible = useMemo(() => {
    let v = items;
    if (category !== ALL) v = v.filter((i) => i.category === category);
    if (kindFilter !== ALL) v = v.filter((i) => i.kind === kindFilter);
    if (search) {
      const s = search.toLowerCase();
      v = v.filter((i) => (i.name || "").toLowerCase().includes(s) || (i.sku || "").toLowerCase().includes(s));
    }
    return v;
  }, [items, category, kindFilter, search]);

  const groups = useMemo(() => {
    const m = {};
    visible.forEach((i) => {
      (m[i.category] = m[i.category] || []).push(i);
    });
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b, "tr"));
  }, [visible]);
  const filtersActive = search.trim() !== "" || kindFilter !== ALL || category !== ALL;
  const isCatOpen = (cat) => openCats[cat] ?? filtersActive;

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const KIND_TR = { product: "Ürün", service: "Servis", depo_disi: "Depo Dışı Ürün" };

  const setItemKind = async (item, newKind) => {
    try {
      const r = await api.post("/settings/product-kinds", { sku: item.sku, kind: newKind });
      setItems((list) => list.map((i) => (i.sku === item.sku ? { ...i, kind: r.data.kind, override: r.data.override } : i)));
      toast.success(`${item.name} artık "${KIND_TR[r.data.kind] || r.data.kind}" olarak işaretli`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const serviceCount = items.filter((i) => i.kind === "service").length;
  const depoCount = items.filter((i) => i.kind === "depo_disi").length;
  const productCount = items.length - serviceCount - depoCount;

  const KPI_CARDS = [
    [ALL, "Tümü", items.length, "border-stone-300", "text-stone-600"],
    ["product", "Ürünler", productCount, "border-[#1C4E35]/40 bg-[#1C4E35]/5", "text-[#1C4E35]"],
    ["service", "Servisler", serviceCount, "border-sky-300 bg-sky-50/50", "text-sky-700"],
    ["depo_disi", "Depo Dışı", depoCount, "border-orange-300 bg-orange-50/50", "text-orange-700"],
  ];

  if (loading) return <p className="text-sm text-stone-500" data-testid="settings-loading">Yükleniyor...</p>;

  const exportOverrides = async () => {
    try {
      const r = await api.get("/settings/kind-overrides/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "cop31_tur_ayarlari.json";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`${Object.keys(r.data.overrides).length} tür ayarı dışa aktarıldı`);
    } catch {
      toast.error("Dışa aktarılamadı");
    }
  };

  const importOverrides = async (file) => {
    try {
      const data = JSON.parse(await file.text());
      const r = await api.post("/settings/kind-overrides/import", { overrides: data.overrides || {} });
      toast.success(`${r.data.applied} tür ayarı içe aktarıldı`);
      const refreshed = await api.get("/settings/product-kinds");
      setItems(refreshed.data.data);
    } catch {
      toast.error("İçe aktarılamadı — geçerli bir JSON dosyası seçin");
    }
  };

  return (
    <div className="space-y-4 fade-up" data-testid="settings-page">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="settings-kpi-cards">
        {KPI_CARDS.map(([val, label, count, cls, txt]) => (
          <button
            key={val}
            type="button"
            onClick={() => setKindFilter((cur) => (cur === val ? ALL : val))}
            data-testid={`settings-kpi-${val === ALL ? "all" : val}`}
            className={`border p-4 text-left transition-all active:scale-[0.98] ${cls} ${kindFilter === val ? "ring-2 ring-[#1C4E35] ring-offset-1" : "hover:opacity-80"}`}
          >
            <p className={`text-[11px] font-bold uppercase tracking-[0.18em] ${txt}`}>{label}</p>
            <p className="mt-1 font-heading text-3xl font-black tracking-tight text-[#0A0D0B]" data-testid={`settings-kpi-count-${val === ALL ? "all" : val}`}>{count}</p>
            <p className="mt-0.5 text-[11px] text-stone-500">ürün</p>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 border border-stone-200 bg-white p-4">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Ürün adı veya SKU ara..."
            data-testid="settings-search"
            className="w-[260px] rounded-sm border-stone-300 pl-8"
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[220px] rounded-sm border-stone-300" data-testid="settings-category-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tüm Kategoriler</SelectItem>
            {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex overflow-hidden border border-stone-300" data-testid="settings-kind-filter">
          {[[ALL, "Tümü"], ["product", "Ürünler"], ["service", "Servisler"], ["depo_disi", "Depo Dışı"]].map(([val, label]) => (
            <button
              key={val}
              type="button"
              onClick={() => setKindFilter(val)}
              data-testid={`settings-kind-${val === ALL ? "all" : val}`}
              className={`px-3 py-2 text-xs font-medium transition-colors ${kindFilter === val ? "bg-[#1C4E35] text-white" : "bg-white text-stone-600 hover:bg-stone-100"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="ml-auto text-xs text-stone-500" data-testid="settings-counts">
          {items.length} ürün · <span className="font-medium text-[#1C4E35]">{serviceCount} servis</span> · <span className="font-medium text-orange-700">{depoCount} depo dışı</span>
        </p>
        <button
          type="button"
          onClick={exportOverrides}
          data-testid="settings-export-button"
          title="Tür ayarlarını JSON olarak indir"
          className="flex items-center gap-1 border border-stone-300 bg-white px-2 py-1 text-[11px] font-medium text-stone-600 transition-colors hover:bg-stone-100 active:scale-95"
        >
          <Download size={12} /> Dışa Aktar
        </button>
        <label
          data-testid="settings-import-button"
          title="Tür ayarlarını JSON dosyasından yükle"
          className="flex cursor-pointer items-center gap-1 border border-stone-300 bg-white px-2 py-1 text-[11px] font-medium text-stone-600 transition-colors hover:bg-stone-100 active:scale-95"
        >
          <Upload size={12} /> İçe Aktar
          <input type="file" accept=".json" className="hidden" onChange={(e) => { if (e.target.files[0]) importOverrides(e.target.files[0]); e.target.value = ""; }} />
        </label>
      </div>

      <div className="border border-stone-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="text-xs">Ürün</TableHead>
              <TableHead className="text-xs">Kategori</TableHead>
              <TableHead className="text-xs">API'den Gelen Tür</TableHead>
              <TableHead className="text-xs">Kaynak</TableHead>
              <TableHead className="text-right text-xs">Tür İşaretle</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-6 text-center text-sm text-stone-400" data-testid="settings-empty">Ürün bulunamadı.</TableCell>
              </TableRow>
            )}
            {groups.map(([cat, list]) => (
              <Fragment key={cat}>
                <TableRow
                  onClick={() => setOpenCats((o) => ({ ...o, [cat]: !isCatOpen(cat) }))}
                  className="cursor-pointer select-none bg-stone-50 hover:bg-stone-100"
                  data-testid={`settings-group-${cat.replace(/\s+/g, "_")}`}
                >
                  <TableCell colSpan={5} className="py-2.5">
                    <span className="flex items-center gap-2">
                      <ChevronDown size={14} className={`text-stone-400 transition-transform duration-200 ${isCatOpen(cat) ? "" : "-rotate-90"}`} />
                      <span className="font-heading text-xs font-black uppercase tracking-[0.15em] text-[#0A0D0B]">{cat}</span>
                      <span className="border border-stone-300 bg-white px-1.5 py-px text-[10px] font-medium text-stone-600">{list.length} ürün</span>
                      {list.some((i) => i.kind === "service") && <span className="border border-sky-300 bg-sky-50 px-1.5 py-px text-[10px] font-medium text-sky-700">{list.filter((i) => i.kind === "service").length} servis</span>}
                      {list.some((i) => i.kind === "depo_disi") && <span className="border border-orange-300 bg-orange-50 px-1.5 py-px text-[10px] font-medium text-orange-700">{list.filter((i) => i.kind === "depo_disi").length} depo dışı</span>}
                    </span>
                  </TableCell>
                </TableRow>
                {isCatOpen(cat) && list.map((i) => (
              <TableRow key={i.sku} data-testid={`settings-row-${i.sku}`} className="hover:bg-stone-50">
                <TableCell className="py-2">
                  <div className="flex items-center gap-2.5">
                    {i.image ? (
                      <img src={mediaUrl(i.image)} alt={i.name} loading="lazy" data-testid={`settings-image-${i.sku}`} className="h-10 w-10 shrink-0 border border-stone-200 bg-white object-contain" />
                    ) : (
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center border border-stone-200 bg-stone-50">
                        {i.kind === "service" ? <Wrench size={13} className="text-sky-600" /> : i.kind === "depo_disi" ? <PackageX size={13} className="text-orange-600" /> : <Package size={13} className="text-stone-400" />}
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-[#0A0D0B]">{i.name}</p>
                      <p className="text-[10px] text-stone-400">{i.sku}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="py-2 text-xs text-stone-600">{i.category}</TableCell>
                <TableCell className="py-2">
                  <span className={`border px-1.5 py-0.5 text-[10px] font-medium ${i.apiKind === "service" ? "border-sky-300 bg-sky-50 text-sky-700" : "border-stone-300 bg-stone-50 text-stone-600"}`}>
                    {i.apiKind === "service" ? "Servis" : "Ürün"}
                  </span>
                  {i.kind === "depo_disi" && (
                    <span className="ml-1 border border-orange-300 bg-orange-50 px-1.5 py-0.5 text-[10px] font-medium text-orange-700" data-testid={`settings-depo-badge-${i.sku}`}>Depo Dışı</span>
                  )}
                </TableCell>
                <TableCell className="py-2">
                  {i.override ? (
                    <span className="border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700" data-testid={`settings-override-${i.sku}`}>Manuel</span>
                  ) : (
                    <span className="text-[10px] text-stone-400">API</span>
                  )}
                </TableCell>
                <TableCell className="py-2 text-right">
                  <div className="inline-flex overflow-hidden border border-stone-300" data-testid={`settings-kind-toggle-${i.sku}`}>
                    {[["product", "Ürün"], ["service", "Servis"], ["depo_disi", "Depo Dışı"]].map(([val, label]) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => i.kind !== val && setItemKind(i, val)}
                        data-testid={`settings-set-${val}-${i.sku}`}
                        className={`px-2 py-1 text-[10px] font-medium transition-colors ${
                          i.kind === val
                            ? val === "service" ? "bg-sky-600 text-white" : val === "depo_disi" ? "bg-orange-600 text-white" : "bg-[#1C4E35] text-white"
                            : "bg-white text-stone-500 hover:bg-stone-100"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </TableCell>
              </TableRow>
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>

      <HubSettingsSection />

      <CustomCategoriesSection />
    </div>
  );
}
